// client/src/components/public/InterestForm.js
import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import useInterestExpression from '../../hooks/useInterestExpression'
import LeagueExplanationModal from '../leagues/LeagueExplanationModal'
import './InterestForm.css'

/**
 * Standalone page for collecting interested user emails
 * League operators can share this link directly to collect leads
 * No authentication or sign-in required
 */
function InterestForm({
  brandName = 'ServeSync Tennis',
  brandTagline = 'Flexible Tennis League Platform',
}) {
  const navigate = useNavigate()
  const [showExplanationModal, setShowExplanationModal] = useState(false)

  const {
    email,
    submitting,
    submitted,
    error,
    emailRef,
    handleEmailChange,
    handleSubmit,
  } = useInterestExpression()

  const handleModalClose = () => {
    setShowExplanationModal(false)
    navigate('/landing')
  }

  return (
    <div className="interest-form-page">
      <div className="interest-form-container">
        <div className="interest-form-content">
          {/* Logo/Branding */}
          <div className="interest-form-header">
            <h1 className="interest-form-title">{brandName}</h1>
            <p className="interest-form-subtitle">{brandTagline}</p>
          </div>

          {!submitted ? (
            <>
              {/* Description */}
              <div className="interest-form-description">
                <h2>Join the Waitlist</h2>
                <p>
                  Interested in joining a flexible tennis league? Enter your
                  email address below and we'll notify you when we open
                  registration.
                </p>
              </div>

              {/* Error Message */}
              {error && (
                <div className="alert alert-danger" role="alert">
                  {error}
                </div>
              )}

              {/* Form */}
              <form onSubmit={handleSubmit} className="interest-form">
                <div className="form-group">
                  <label htmlFor="email" className="form-label">
                    Email Address
                  </label>
                  <input
                    ref={emailRef}
                    id="email"
                    type="email"
                    value={email}
                    onChange={handleEmailChange}
                    className="form-control form-control-lg"
                    placeholder="your.email@example.com"
                    disabled={submitting}
                    required
                    autoFocus
                    aria-label="Email address"
                  />
                  <small className="form-text text-muted">
                    We'll only use your email to notify you about league
                    availability
                  </small>
                </div>

                <button
                  type="submit"
                  className="btn btn-primary btn-lg w-100"
                  disabled={submitting || email.trim() === ''}
                >
                  {submitting ? 'Submitting...' : 'Join Waitlist'}
                </button>
              </form>

              {/* Additional Info */}
              <div className="interest-form-footer">
                <p className="text-muted small">
                  Already have an account?{' '}
                  <Link to="/sign-in" className="interest-form-link">
                    Sign In
                  </Link>
                </p>
              </div>
            </>
          ) : (
            /* Success State */
            <div className="interest-form-success">
              <div className="success-icon">
                <svg
                  width="64"
                  height="64"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="var(--fl-theme-primary)"
                    strokeWidth="2"
                  />
                  <path
                    d="M8 12L11 15L16 9"
                    stroke="var(--fl-theme-primary)"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h2>Thank You!</h2>
              <p className="success-message">
                We've received your information and will contact you soon with
                next steps.
              </p>
              <div className="success-actions">
                <button
                  onClick={() => setShowExplanationModal(true)}
                  className="btn btn-secondary"
                  type="button"
                >
                  Learn More About ServeSync
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* League Explanation Modal */}
      <LeagueExplanationModal
        isOpen={showExplanationModal}
        onClose={handleModalClose}
        ctaButtonText="Got it, take me home 🏠"
      />
    </div>
  )
}

export default InterestForm
